import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

export default function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  trendValue,
  iconBgColor = 'bg-primary-500',
  borderColor,
  hoverBgColor,
  textColor,
  showPeriodToggle = false,
  periodType,
  currentPeriod,
  onPeriodChange,
  className
}) {
  const valueRef = useRef(null);
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState(24); // Starting with default size (2xl)

  useEffect(() => {
    const resizeText = () => {
      if (!valueRef.current || !containerRef.current) return;
      
      const container = containerRef.current;
      const element = valueRef.current;
      
      // Reset font size to calculate proper width
      element.style.fontSize = '24px';
      
      // Get container and element widths
      const containerWidth = container.offsetWidth;
      const textWidth = element.scrollWidth;
      
      if (textWidth > containerWidth) {
        // Calculate new font size
        const ratio = containerWidth / textWidth;
        const newSize = Math.floor(24 * ratio);
        // Set minimum font size of 12px
        setFontSize(Math.max(12, newSize));
      } else {
        setFontSize(24);
      }
    };

    resizeText();
    window.addEventListener('resize', resizeText);
    
    return () => window.removeEventListener('resize', resizeText);
  }, [value]); // Re-run when value changes

  // Helper function to determine font size based on content length
  const getFontSize = () => {
    if (!value) return 'text-2xl';
    const length = value.toString().length;
    
    if (length <= 6) return 'text-2xl'; // Regular size for normal numbers
    if (length <= 9) return 'text-xl';  // Slightly smaller for larger numbers
    if (length <= 12) return 'text-lg'; // Even smaller for very large numbers
    return 'text-base';                 // Base size for extremely large numbers
  };

  return (
    <div className={`bg-white dark:bg-[#2E3B4E] shadow rounded-lg p-6 ${className}`}>
      <dt>
        <div className={`absolute rounded-md ${iconBgColor} p-2 sm:p-3`}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" aria-hidden="true" />
        </div>
        <p className="ml-12 sm:ml-16 truncate text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
          {title}
        </p>
      </dt>
      <dd className="ml-12 sm:ml-16">
        <div className="flex items-center">
          <p className={`${getFontSize()} font-semibold text-gray-900 dark:text-white break-all`}>
            {value}
          </p>
        </div>
        
        {trend && !showPeriodToggle && (
          <div className="mt-4">
            <span className={`text-sm ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {trendValue}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
              vs last month
            </span>
          </div>
        )}

        {showPeriodToggle && (
          <div className="flex justify-end mt-4">
            <div className="flex w-[200px]">
              <button
                onClick={() => onPeriodChange('day', periodType)}
                className={`flex-1 px-2 py-1 text-[10px] border ${borderColor}
                  ${currentPeriod === 'day'
                    ? `${iconBgColor} text-white`
                    : `bg-white dark:bg-gray-700 ${textColor} dark:text-primary-400`
                  } rounded-l-md ${hoverBgColor} hover:text-white transition-colors`}
              >
                Day
              </button>
              <button
                onClick={() => onPeriodChange('week', periodType)}
                className={`flex-1 px-2 py-1 text-[10px] border-y ${borderColor}
                  ${currentPeriod === 'week'
                    ? `${iconBgColor} text-white`
                    : `bg-white dark:bg-gray-700 ${textColor} dark:text-primary-400`
                  } ${hoverBgColor} hover:text-white transition-colors -mx-[1px]`}
              >
                Week
              </button>
              <button
                onClick={() => onPeriodChange('month', periodType)}
                className={`flex-1 px-2 py-1 text-[10px] border ${borderColor}
                  ${currentPeriod === 'month'
                    ? `${iconBgColor} text-white`
                    : `bg-white dark:bg-gray-700 ${textColor} dark:text-primary-400`
                  } rounded-r-md ${hoverBgColor} hover:text-white transition-colors`}
              >
                Month
              </button>
            </div>
          </div>
        )}
      </dd>
    </div>
  );
}